// eslint-disable-next-line import/named
import { customElement } from 'lit/decorators.js';
import { DataBinding, getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import {
  DataTracker,
  DynamicValueBinder,
  EventValue,
  EventValueGetter,
  EventValueSetter,
  FieldType
} from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { Project } from '../../api/dealer-api-interface-project';
import { ProjectContainerManager } from '../data/project-container';
import { LangProjectDetailView } from '../../../localization/scripts/projects/views/project-detail-view-lang';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';

export interface ProjectDetailViewOptions {
  projectManager: ProjectContainerManager;
}

@customElement('wm-projectdetailview')
export class ProjectDetailView extends ViewBase {
  projectId: string = emptyGuid;
  projectManager: ProjectContainerManager;
  internalId: string;
  dataBinding: DataBinding;
  dataTracker: DataTracker;
  forms: FormInputAssistant;

  constructor(options: ProjectDetailViewOptions) {
    super();
    this.internalId = getInternalId();
    this.projectManager = options.projectManager;

    this.dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `project-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(this.dataBinding);
    this.forms = new FormInputAssistant(this.dataTracker, () => this.projectManager.isReadonly());
    this.forms.immediateBindingUpdate = true;
    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.project),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    const addDynamicMoney = (
      fieldName: string,
      getter: EventValueGetter,
      setter: EventValueSetter,
      readonly?: () => boolean
    ) => {
      this.dataTracker.addBinding(
        new DynamicValueBinder(FieldType.money, true, getter, setter, readonly),
        fieldName,
        fieldName,
        FieldType.money,
        true
      );
    };

    addField('title', FieldType.string);
    addField('description', FieldType.string, true);

    addDynamicMoney(
      'budget',
      () => this.project.budget,
      (_value: EventValue) => (this.project.budget = _value as number),
      () => true
    );
  }

  get project(): Project {
    return this.projectManager.project;
  }

  public async prepareForSave(): Promise<void> {
    this.dataTracker.applyChangeToValue();
  }

  public async loadOrRefresh(): Promise<void> {
    await this.projectManager.needsProject();

    this.requestUpdate();
  }

  public getValidationErrors() {
    const errors: string[] = [];

    const title = this.dataBinding.getValue('title');

    if (isEmptyOrSpace(title)) errors.push(LangProjectDetailView.provideProjectTitle());

    return errors;
  }

  protected template(): EventTemplate {
    const forms = this.forms;
    return html`
      <form class="frm-quote-details form-two-col">
        <div class="row">
          <div>
            ${forms.textRequired('title', LangProjectDetailView.projectTitle(), 100)}
            ${forms.money('budget', LangProjectDetailView.projectBudget())}
            ${forms.note('description', LangProjectDetailView.projectDescription(), 3000)}
          </div>
          <div>
            ${forms.dateReadonly('dateCreated', LangProjectDetailView.created())}
            ${forms.dateReadonly('lastModifiedDate', LangProjectDetailView.lastModified())}
          </div>
        </div>
      </form>
    `;
  }
}
